import { useRevuesNumeriques } from "../lib/hooks/useRevuesNumeriques";
import Title from "../components/ui/Title";
import RevueNumeriqueItem from "../components/revues-numeriques/RevueNumeriqueItem";
import { Skeleton } from "../components/ui/skeleton";
import { Alert } from "../components/ui/alert";
import GooglePlayIcon from "../assets/img/googleplay.png";
import AppStoreIcon from "../assets/img/appstore.png";
import { BookOpen, CalendarClock, Inbox, LibraryBig, Smartphone } from "lucide-react";

const LoadingIssues = () => (
  <div className="divide-y divide-slate-100">
    {[0, 1, 2].map((item) => (
      <div key={item} className="flex gap-5 px-5 py-6 sm:px-6">
        <Skeleton className="h-40 w-28 shrink-0 rounded-xl" />
        <div className="flex flex-1 flex-col gap-3 py-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-full max-w-xl" />
          <Skeleton className="mt-2 h-9 w-36" />
        </div>
      </div>
    ))}
  </div>
);

export default function RevuesNumeriquesPage() {
  const { data: revuesNumeriques, isError } = useRevuesNumeriques();

  return (
    <div className="mx-auto w-full max-w-7xl pb-8">
      <div className="mb-7">
        <Title>Mes revues numériques</Title>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 md:text-base">
          Feuilletez les numéros de L'Abeille de France inclus dans votre abonnement.
        </p>
      </div>

      {isError && (
        <Alert variant="destructive" className="mb-6">
          Impossible de charger vos revues. Réessayez dans quelques instants.
        </Alert>
      )}

      <section className="mb-7 overflow-hidden rounded-3xl bg-[#173f35] text-white shadow-[0_18px_55px_-35px_rgba(23,63,53,0.9)]">
        <div className="relative grid gap-7 px-5 py-7 sm:px-7 lg:grid-cols-[1fr_auto] lg:items-center lg:px-8">
          <div className="absolute -right-14 -top-20 h-48 w-48 rounded-full border border-[#d7b64d]/25" />
          <div className="relative flex gap-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/10">
              <BookOpen className="h-6 w-6 text-[#f4d56f]" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Votre revue, partout avec vous</h2>
              <div className="mt-2 flex max-w-xl items-start gap-2 text-sm leading-6 text-emerald-50/80">
                <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-[#f4d56f]" />
                <p>Chaque nouveau numéro est généralement disponible entre le 5 et le 10 du mois.</p>
              </div>
            </div>
          </div>

          <div className="relative border-t border-white/10 pt-5 lg:border-l lg:border-t-0 lg:pl-7 lg:pt-0">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-emerald-50/90">
              <Smartphone className="h-4 w-4 text-[#f4d56f]" />
              Lecture confortable sur mobile et tablette
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://play.google.com/store/apps/details?id=com.labeilledefrance.revue"
                target="_blank"
                rel="noreferrer"
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4d56f] focus-visible:ring-offset-2 focus-visible:ring-offset-[#173f35]"
              >
                <img src={GooglePlayIcon} alt="Télécharger sur Google Play" className="h-10 w-auto" />
              </a>
              <a
                href="https://apps.apple.com/fr/app/labeille-de-france/id6739069067"
                target="_blank"
                rel="noreferrer"
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f4d56f] focus-visible:ring-offset-2 focus-visible:ring-offset-[#173f35]"
              >
                <img src={AppStoreIcon} alt="Télécharger dans l'App Store" className="h-10 w-auto" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {!isError && (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_40px_-34px_rgba(15,23,42,0.5)]">
          <div className="flex items-center gap-3 border-b border-slate-100 bg-[#fbfaf6] px-5 py-4 sm:px-6">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
              <LibraryBig className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-bold text-slate-950">Votre bibliothèque</h2>
              <p className="text-sm text-slate-500">Tous les numéros accessibles avec votre abonnement</p>
            </div>
          </div>

          {!revuesNumeriques ? (
            <LoadingIssues />
          ) : revuesNumeriques.length === 0 ? (
            <div className="flex flex-col items-center px-5 py-12 text-center text-slate-500">
              <Inbox className="mb-3 h-8 w-8 text-slate-300" />
              <span className="font-medium text-slate-700">Aucune revue disponible</span>
              <span className="mt-1 max-w-md text-sm">
                Votre bibliothèque sera alimentée selon les droits liés à votre abonnement.
              </span>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {revuesNumeriques.map((revue) => (
                <RevueNumeriqueItem key={revue.id} revue={revue} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
